import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Chip,
  Stack,
} from '@mui/material';
import { WarningAmber as WarningIcon } from '@mui/icons-material';
import { getEnvironment, environmentDisplayName, getEnvironmentHostname } from '../../config/environment';

/**
 * Non-production login warning.
 *
 * Anyone who signs in to a NON-production environment (local dev or staging)
 * gets an explicit modal saying so, because the two surfaces are visually
 * near-identical to production and it is otherwise easy to believe you are
 * working with real data — or, worse, to believe production data is safe while
 * changing staging (and vice-versa).
 *
 * Environment comes from the shared hostname-based detector
 * (`config/environment.ts`), which is also what drives the AppBar pill and the
 * favicon badge, so all three agree by construction. In production
 * `isProduction` is true and this component renders nothing at all.
 *
 * Shown **once per browser session** (sessionStorage), so it fires on login and
 * on a fresh tab, but does not nag on every route change or re-render. Closing
 * the browser (or opening a new tab) surfaces it again — deliberate: the point
 * is that each new working session starts with the reminder.
 *
 * Mounted inside the authenticated Layout, so it can only appear to a
 * signed-in user.
 */

const SESSION_KEY = 'env_warning_acknowledged';

const EnvironmentWarningDialog: React.FC = () => {
  const env = getEnvironment();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (env.isProduction) return;
    // sessionStorage can throw in hardened/private modes — never let the
    // warning break the app; fall back to showing it.
    let acknowledged = false;
    try {
      acknowledged = sessionStorage.getItem(SESSION_KEY) === 'true';
    } catch {
      acknowledged = false;
    }
    if (!acknowledged) setOpen(true);
  }, [env.isProduction]);

  const handleClose = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(SESSION_KEY, 'true');
    } catch {
      // Non-fatal: the dialog simply reappears on the next mount.
    }
  };

  if (env.isProduction || !env.badge) return null;

  const name = environmentDisplayName(env.key);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="env-warning-title"
    >
      <DialogTitle id="env-warning-title">
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarningIcon sx={{ color: env.badge.bg }} />
          <Box component="span">Non-production environment</Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box>
            <Chip
              label={name}
              sx={{
                bgcolor: env.badge.bg,
                color: env.badge.fg,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
              size="small"
            />
          </Box>
          <DialogContentText>
            You are signed in to the <strong>{name}</strong> environment — this is
            <strong> not production</strong>. Data here may be incomplete, stale, or
            test-only, and anything you change will not affect production.
          </DialogContentText>
          <DialogContentText variant="body2" color="text.secondary">
            Host: <code>{getEnvironmentHostname()}</code>
          </DialogContentText>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleClose} autoFocus>
          I understand
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnvironmentWarningDialog;
